/**
 * Trinetra Evaluated Posture (TEP) — org dashboard.
 *
 * Two views in one page:
 *  1. Live preview — calls /tep/preview to compute posture against
 *     current open findings + active frameworks. Cheap and live.
 *  2. Persisted snapshots — calls /tep/snapshots; org admins build
 *     these on demand and they stay around as audit-trail artifacts.
 *
 * No nightly cron, no background jobs — every refresh is org-triggered.
 */
import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck, Loader2, AlertCircle, RefreshCw, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, MinusCircle, Database,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCssVars } from '@/hooks/use-css-vars';

interface ControlEntry {
  scfId: string;
  domain: string;
  name: string;
  state: 'PASS' | 'FAIL' | 'NE' | 'NA';
  mappedTemplates: string[];
  openFindings: Array<{
    findingId: string; title: string; severity: string;
    templateId: string | null; projectId: string | null; createdAt: string | null;
  }>;
  clauses: Array<{ frameworkId: string; clauseId: string; clauseTitle: string | null; relationship: string }>;
  severityRollup: { critical: number; high: number; medium: number; low: number; info: number };
}

interface FrameworkRollup {
  frameworkId: string; shortName: string; name: string;
  evaluated: number; passed: number; failed: number;
  notEvaluated: number; notApplicable: number; totalInFramework: number;
}

interface Payload {
  scfVersion: string;
  frameworks: string[];
  generatedAt: string;
  totalOpenFindings: number;
  perFramework: FrameworkRollup[];
  controls: ControlEntry[];
}

export function TepPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const qc = useQueryClient();
  const [activeFramework, setActiveFramework] = useState<string | 'all'>('all');

  const { data: previewRes, isLoading, refetch } = useQuery({
    queryKey: ['org', orgId, 'tep', 'preview'],
    queryFn: () => apiClient.previewTepPosture(orgId!),
    enabled: !!orgId,
  });
  const payload = previewRes?.data as Payload | null | undefined;

  const { data: snapsRes } = useQuery({
    queryKey: ['org', orgId, 'tep', 'snapshots'],
    queryFn: () => apiClient.listTepSnapshots(orgId!),
    enabled: !!orgId,
  });
  const snapshots = (snapsRes?.data as Array<{ snapshotId: string; createdAt: string; status: string; frameworks: string[]; summary: { totalOpenFindings?: number } | null }> | undefined) ?? [];

  const build = useMutation({
    mutationFn: () => apiClient.buildTepSnapshot(orgId!),
    onSuccess: () => {
      toast.success('Posture snapshot built');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'tep'] });
      void refetch();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Filtered control list per the framework picker.
  const visibleControls = useMemo(() => {
    if (!payload) return [] as ControlEntry[];
    if (activeFramework === 'all') return payload.controls;
    return payload.controls.filter((c) => c.clauses.some((cl) => cl.frameworkId === activeFramework));
  }, [payload, activeFramework]);

  if (!orgId) return null;

  // ─── Empty / not-configured states ─────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <Card className="p-8 text-center text-muted-foreground">Computing live posture…</Card>
      </div>
    );
  }
  if (!payload) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold">No frameworks selected for this org.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Pick one or more compliance frameworks in Org Settings → TEP, then come back here to see your posture.
              </p>
              <Link to={`/orgs/${orgId}/settings`} className="text-xs text-primary hover:underline mt-2 inline-block">
                Open TEP settings →
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ─── Main render ───────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <PageHeader />

      {/* Per-framework rollup tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {payload.perFramework.map((f) => {
          const denom = f.evaluated || 1;
          const pct = Math.round((f.passed / denom) * 100);
          const coverage = Math.round((f.evaluated / Math.max(f.totalInFramework, 1)) * 100);
          return (
            <Card key={f.frameworkId} className="p-4 cursor-pointer transition-colors hover:border-primary/40"
              onClick={() => setActiveFramework((prev) => prev === f.frameworkId ? 'all' : f.frameworkId)}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-sm font-bold text-foreground">{f.shortName}</p>
                <span className={cn(
                  'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full',
                  activeFramework === f.frameworkId ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                )}>
                  {activeFramework === f.frameworkId ? 'filtered' : 'click to filter'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-1 mb-3">{f.name}</p>

              {/* Two headline metrics side by side: coverage = how much of
                  the framework Trinetra actually observes; pass-rate =
                  within that observed slice, what's healthy. Coverage is
                  given equal visual weight so users can't mistake a high
                  pass % on a low-coverage framework for full compliance. */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-3xl font-bold text-saffron tabular-nums leading-none">{coverage}%</p>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">Coverage</p>
                  <p className="text-[11px] text-muted-foreground">{f.evaluated} / {f.totalInFramework} controls</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground tabular-nums leading-none">{pct}%</p>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">Pass rate</p>
                  <p className="text-[11px] text-muted-foreground">within evaluated</p>
                </div>
              </div>

              {/* Coverage bar — visual reinforcement of the % above. */}
              <CoverageBar pct={coverage} />

              <div className="grid grid-cols-4 gap-2 text-center">
                <Stat n={f.passed} label="pass" tone="green" />
                <Stat n={f.failed} label="fail" tone="red" />
                <Stat n={f.notEvaluated} label="N/E" tone="muted" />
                <Stat n={f.totalInFramework} label="total" tone="navy" />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Snapshot bar */}
      <Card className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-muted-foreground">
          Live preview generated at{' '}
          <span className="text-foreground font-mono">{new Date(payload.generatedAt).toLocaleString()}</span>
          {' · '}SCF <span className="text-foreground font-mono">{payload.scfVersion}</span>
          {' · '}<span className="text-foreground font-semibold">{payload.totalOpenFindings}</span> open findings considered.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh preview
          </Button>
          <Button size="sm" onClick={() => build.mutate()} disabled={build.isPending}>
            {build.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Database className="h-3.5 w-3.5 mr-1.5" />}
            Build snapshot
          </Button>
        </div>
      </Card>

      {/* Control list */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold">
            Controls
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {visibleControls.length} {activeFramework !== 'all' ? `in ${activeFramework}` : 'in scope'}
            </span>
          </p>
          {activeFramework !== 'all' && (
            <button onClick={() => setActiveFramework('all')} className="text-xs text-primary hover:underline">
              clear filter
            </button>
          )}
        </div>
        <div className="divide-y divide-border">
          {visibleControls
            .slice()
            .sort((a, b) => stateOrder(a.state) - stateOrder(b.state) || a.scfId.localeCompare(b.scfId))
            .map((c) => (
              <ControlRow key={c.scfId} control={c} activeFramework={activeFramework} />
            ))}
          {visibleControls.length === 0 && (
            <p className="px-5 py-8 text-sm text-muted-foreground text-center">No controls match the current filter.</p>
          )}
        </div>
      </Card>

      {/* Snapshot history */}
      {snapshots.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <p className="text-sm font-semibold">Snapshot history</p>
            <p className="text-xs text-muted-foreground">Audit-trail artifacts. Each row is the org's posture as of that moment.</p>
          </div>
          <div className="divide-y divide-border">
            {snapshots.map((s) => (
              <div key={s.snapshotId} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-mono text-foreground">{new Date(s.createdAt).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.status} · {s.frameworks?.length ?? 0} frameworks{s.summary?.totalOpenFindings != null ? ` · ${s.summary.totalOpenFindings} open findings` : ''}
                  </p>
                </div>
                <span className={cn(
                  'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full',
                  s.status === 'ready' ? 'bg-india-green/15 text-india-green' :
                    s.status === 'failed' ? 'bg-red-500/15 text-red-600 dark:text-red-400' :
                      'bg-muted text-muted-foreground',
                )}>
                  {s.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <p className="text-[11px] text-muted-foreground italic px-1">
        TEP reflects what Trinetra can evaluate from your scans. Controls labelled <strong>N/E</strong> aren't covered by Trinetra and require attestation outside the platform — TEP is not a compliance attestation.
      </p>
    </div>
  );
}

function PageHeader() {
  // Breadcrumbs render globally from app-layout via the shared
  // <Breadcrumbs /> component — adding the /tep path to its routing
  // logic is enough; no inline breadcrumb needed here.
  return (
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-xl bg-india-green/15 flex items-center justify-center">
        <ShieldCheck className="h-5 w-5 text-india-green" />
      </div>
      <div className="flex-1 min-w-0">
        <h1 className="text-2xl font-bold text-foreground">Trinetra Evaluated Compliance Posture</h1>
        <p className="text-sm text-muted-foreground">
          Your security findings, mapped through the Secure Controls Framework to the compliance regimes you care about. Trinetra evaluates a defined subset of every framework — areas it cannot directly observe (governance, HR, physical security, vendor management, training) are reported as <span className="font-medium text-foreground">Not Evaluated</span> rather than assumed compliant. Treat this as evidence, not attestation.
        </p>
      </div>
    </div>
  );
}

// Width is data-driven, but CSP `style-src-attr 'none'` blocks inline
// `style={{ width }}` props. useCssVars exposes the value as a CSS
// custom property via setProperty (exempt from style-src-attr); the
// arbitrary-value Tailwind class then references it.
function CoverageBar({ pct }: { pct: number }) {
  const ref = useCssVars<HTMLDivElement>({ '--bar-width': `${Math.max(0, Math.min(100, pct))}%` });
  return (
    <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden mb-3">
      <div ref={ref} className="h-full rounded-full bg-saffron transition-all w-[var(--bar-width)]" />
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone: 'green' | 'red' | 'muted' | 'navy' }) {
  const cls = tone === 'green' ? 'text-india-green' : tone === 'red' ? 'text-red-600 dark:text-red-400' : tone === 'navy' ? 'text-chakra-navy dark:text-blue-400' : 'text-muted-foreground';
  return (
    <div>
      <p className={cn('text-lg font-bold', cls)}>{n}</p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

function stateOrder(s: 'PASS' | 'FAIL' | 'NE' | 'NA'): number {
  return s === 'FAIL' ? 0 : s === 'PASS' ? 1 : s === 'NE' ? 2 : 3;
}

function ControlRow({ control, activeFramework }: { control: ControlEntry; activeFramework: string | 'all' }) {
  const [open, setOpen] = useState(control.state === 'FAIL');
  const visibleClauses = activeFramework === 'all' ? control.clauses : control.clauses.filter((c) => c.frameworkId === activeFramework);
  return (
    <div className="px-5 py-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-3 text-left"
      >
        <span className="mt-0.5 shrink-0">
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </span>
        <span className="shrink-0 mt-0.5">
          <StateBadge state={control.state} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            <span className="font-mono text-xs text-muted-foreground mr-2">{control.scfId}</span>
            {control.name}
          </p>
          <p className="text-xs text-muted-foreground">{control.domain}</p>
        </div>
        {control.state === 'FAIL' && (
          <span className="text-xs font-mono text-red-600 dark:text-red-400 shrink-0">
            {control.openFindings.length} finding{control.openFindings.length === 1 ? '' : 's'}
          </span>
        )}
      </button>

      {open && (
        <div className="ml-10 mt-3 space-y-3 text-sm">
          {control.openFindings.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Open findings driving FAIL</p>
              <ul className="space-y-1">
                {control.openFindings.map((f) => (
                  <li key={f.findingId} className="text-xs">
                    <span className={cn(
                      'inline-block px-1.5 py-0.5 rounded text-[10px] uppercase font-bold mr-2 align-middle',
                      f.severity === 'critical' || f.severity === 'high'
                        ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                        : 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-500',
                    )}>{f.severity}</span>
                    <span className="text-foreground">{f.title}</span>
                    {f.templateId && <span className="text-muted-foreground ml-2 font-mono">[{f.templateId}]</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {control.mappedTemplates.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Trinetra evidence sources</p>
              <p className="text-xs text-muted-foreground font-mono">{control.mappedTemplates.join(', ')}</p>
            </div>
          )}
          {visibleClauses.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Framework clauses</p>
              <ul className="space-y-1">
                {visibleClauses.map((c) => (
                  <li key={`${c.frameworkId}-${c.clauseId}`} className="text-xs">
                    <span className="font-mono text-saffron mr-2">{c.frameworkId}</span>
                    <span className="text-foreground">{c.clauseId}</span>
                    {c.clauseTitle && <span className="text-muted-foreground ml-2">— {c.clauseTitle}</span>}
                    <span className="text-[10px] text-muted-foreground ml-2 uppercase tracking-wider">({c.relationship})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StateBadge({ state }: { state: 'PASS' | 'FAIL' | 'NE' | 'NA' }) {
  if (state === 'PASS') {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-india-green/15 text-india-green"><CheckCircle2 className="h-3 w-3" /> pass</span>;
  }
  if (state === 'FAIL') {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-400"><XCircle className="h-3 w-3" /> fail</span>;
  }
  if (state === 'NE') {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground"><MinusCircle className="h-3 w-3" /> N/E</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">N/A</span>;
}
